import React, { useContext, useEffect, useState } from 'react';
// contexts
import { CurrentProjectContext } from 'contexts/CurrentProjectContext';
import { LastAddedItemContext } from 'contexts/LastAddedItemContext';
import { RunContext } from 'contexts/RunContext';
// gravity components
import { ViewContext } from 'components/lib';
// material ui
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

// tree table
import { Cell, Row } from '@table-library/react-table-library/table';
import { CellTree } from '@table-library/react-table-library/tree';
// services
import { doUppercaseFirstLetter } from 'utils/doUppercaseFirstLetter';
// my components
import {
  CheckedIcon,
  DeleteIcon,
  getSubSimulationIcons,
  MetricsIcon,
  UnCheckedIcon,
  VerticalDotsIcon,
  type SubSimulationCategory,
} from 'components/icons';
import { SubsimStatus } from './RenderStatus';
import EditableLabelState from '../components/EditableLabelState';
import type { ProjectNode, SubSimulation as SubSimulationType, SimulationNode } from 'types/project';
import { deleteSimulation, updateSimulation } from '../../../api/simulationApi';
import { formatBytes } from '../../../utils/formatBytes';

const RowComponent = Row as unknown as React.FC<any>;
const CellTreeComponent = CellTree as unknown as React.FC<any>;
const VerticalDotsIconComponent = VerticalDotsIcon as React.ComponentType<any>;
const DeleteIconComponent = DeleteIcon as React.ComponentType<any>;
const SUBSIMULATION_SECTION_ORDER = ['SUN', 'FREEVIEW', 'TARGETVIEW', 'NOISE', 'WIND', 'SAVED', 'MIXED'] as const;
const SUBSIMULATION_SECTION_INDEX = SUBSIMULATION_SECTION_ORDER.reduce<Record<string, number>>((acc, key, index) => {
  acc[key] = index;
  return acc;
}, {});

const compareSubSimulationIds = (left: SubSimulationType, right: SubSimulationType) =>
  String(left?.id ?? '').localeCompare(String(right?.id ?? ''), undefined, { numeric: true, sensitivity: 'base' });

const SubSimulation: React.FC<{
  simulation: SimulationNode;
  subSimulationSizes: Record<string, number>;
  isDeleteDisabled: boolean;
  showDeletedItems: boolean;
  showSizes: boolean;
}> = ({ simulation, subSimulationSizes, isDeleteDisabled, showDeletedItems, showSizes }) => {
  const context = useContext(ViewContext);
  const { lastAddedItem } = useContext(LastAddedItemContext);
  const { allSubSimulations, setAllSimulations, updateSubSimulationActive } = useContext(RunContext);

  const [selectedItem, setSelectedItem] = useState<SubSimulationType | null>(null);
  const [categorizedSubSimulation, setCategorizedSubSimulations] = useState<Record<string, SubSimulationType[]>>({});
  const [anchorElActionsDetail, setAnchorElActionsDetail] = useState<null | HTMLElement>(null);
  const [subSimulations, setSubSimulations] = useState<SubSimulationType[]>(
    (allSubSimulations[simulation.id] || simulation.subSimulations || []).filter(
      (subSimulation) => showDeletedItems || !subSimulation.deletedAt
    )
  );
  useEffect(() => {
    setSubSimulations(
      (allSubSimulations[simulation.id] || simulation.subSimulations || []).filter(
        (subSimulation) => showDeletedItems || !subSimulation.deletedAt
      )
    );
  }, [allSubSimulations, showDeletedItems, simulation]);

  const handleClickActionsDetail = (event: React.MouseEvent<HTMLElement>, item: SubSimulationType) => {
    event.stopPropagation();
    setAnchorElActionsDetail(event.currentTarget);
    setSelectedItem(item);
  };

  const handleCloseActionsDetail = () => {
    setAnchorElActionsDetail(null);
  };

  const handleMenuCloseActionsDetail = (_event: {}, _reason: 'backdropClick' | 'escapeKeyDown') => {
    handleCloseActionsDetail();
  };
  const { currentProject, setCurrentProject } = useContext(CurrentProjectContext);

  const handleDeleteSubSimulation = (pItem: SubSimulationType) => {
    if (!currentProject) {
      return;
    }
    context.modal.show(
      {
        title: `Delete ${doUppercaseFirstLetter(pItem.type)}`,
        form: {},
        buttonText: 'Delete',
        text: `Are you sure you want to delete ${pItem.name}?`,
        customSubmit: () => deleteSimulation(String(pItem.simulationSetId), pItem.id),
        destructive: true,
      },
      () => {
        const updatedCurrentProject: ProjectNode = { ...currentProject };
        switch (pItem.type) {
          case 'subsimulation':
            updatedCurrentProject.grids = updatedCurrentProject.grids.map((grid) => ({
              ...grid,
              geodatas: grid.geodatas.map((geodata) => ({
                ...geodata,
                simulations: geodata.simulations.map((simulation) =>
                  simulation.id !== pItem.simulationSetId
                    ? simulation
                    : {
                        ...simulation,
                        subSimulations: simulation.subSimulations.filter(
                          (subsimulation) => subsimulation.id !== pItem.id
                        ),
                      }
                ),
              })),
            }));
            break;
          default:
            console.warn('not implemented yet!');
            break;
        }
        setCurrentProject(updatedCurrentProject);
        context.notification.show(pItem.name + ' was deleted', 'success', true, 'toast');
        setAllSimulations({
          ...allSubSimulations,
          [pItem.simulationSetId]: subSimulations.filter((subsimulation) => subsimulation.id !== pItem.id),
        });
      }
    );
    handleCloseActionsDetail();
  };

  useEffect(() => {
    const categorizeBySimKind = (items: SubSimulationType[]) => {
      const categorizedItems = items.reduce<Record<string, SubSimulationType[]>>((acc, item) => {
        const simType = String(item?.simType ?? '');

        if (!acc[simType]) {
          acc[simType] = [];
        }

        acc[simType].push(item);

        return acc;
      }, {});

      const sortedEntries = (Object.entries(categorizedItems) as [string, SubSimulationType[]][])
        .map(
          ([category, categoryItems]) =>
            [category, [...categoryItems].sort(compareSubSimulationIds)] as [string, SubSimulationType[]]
        )
        .sort(([leftCategory], [rightCategory]) => {
          const leftIndex = SUBSIMULATION_SECTION_INDEX[leftCategory] ?? Number.MAX_SAFE_INTEGER;
          const rightIndex = SUBSIMULATION_SECTION_INDEX[rightCategory] ?? Number.MAX_SAFE_INTEGER;
          if (leftIndex !== rightIndex) {
            return leftIndex - rightIndex;
          }
          return leftCategory.localeCompare(rightCategory);
        });
      return Object.fromEntries(sortedEntries);
    };

    if (subSimulations) setCategorizedSubSimulations(categorizeBySimKind(subSimulations));
  }, [subSimulations]);

  return (
    <>
      {Object.keys(categorizedSubSimulation).map((key) => {
        const itemIcon = getSubSimulationIcons(key as SubSimulationCategory);
        const categoryItem = { id: `category_${key}_${simulation.id}` };
        return (
          <React.Fragment key={`category_fragment_${key}_${simulation.id}`}>
            <RowComponent item={categoryItem} className="subsimulation-row fake-subsimulation-row">
              <CellTreeComponent item={categoryItem} style={{ marginLeft: '-20px', padding: 0 }}>
                <span className="subsimulation-category-icon">{itemIcon}</span>
              </CellTreeComponent>
              <Cell> </Cell>
              <Cell> </Cell>
              <Cell> </Cell>
              <Cell> </Cell>
              <Cell> </Cell>
              <Cell> </Cell>
            </RowComponent>

            {categorizedSubSimulation[key].map((subSimulation) => {
              const hasSucceeded = subSimulation.status === 'SUCCEEDED';
              const metricsTooltip = subSimulation.metrics ? JSON.stringify(subSimulation.metrics) : null;
              const subSimulationSize = subSimulationSizes[`${String(simulation.id)}:${String(subSimulation.id)}`];
              return (
                <RowComponent
                  key={`subSim_${subSimulation.simulationSetId}_${subSimulation.id}`}
                  item={subSimulation}
                  className={`${subSimulation.type}-row`}
                  style={{
                    background:
                      subSimulation?.id === lastAddedItem?.id ? 'linear-gradient(to right,#333399,#ff00cc)' : '',
                    padding: 0, // Remove padding
                  }}
                >
                  <CellTreeComponent item={subSimulation}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                      <EditableLabelState
                        item={subSimulation}
                        onSave={(value) =>
                          updateSimulation(String(subSimulation.simulationSetId), subSimulation.id, { name: value })
                        }
                        useTruncation
                      />
                    </div>
                  </CellTreeComponent>
                  <Cell>
                    {(hasSucceeded || subSimulation.simType === 'SAVED' || subSimulation.simType === 'MIXED') && (
                      <Checkbox
                        checkedIcon={<CheckedIcon />}
                        icon={<UnCheckedIcon />} // Custom icon for unchecked state
                        className="subsimulation-checkbox"
                        checked={!!subSimulation.isActive} // Convert to boolean
                        onChange={async (e) => {
                          await updateSubSimulationActive(
                            subSimulation.simulationSetId,
                            subSimulation.id,
                            e.target.checked
                          );

                          context.notification.show(
                            `${subSimulation.name} has been ${!e.target.checked ? 'activated' : 'deactivated'}`,
                            'success',
                            true,
                            'toast'
                          );
                        }}
                      />
                    )}
                  </Cell>
                  <Cell>{<SubsimStatus subSimulation={subSimulation} simulation={simulation} />}</Cell>
                  <Cell>
                    <div className="center-cell">{showSizes ? formatBytes(subSimulationSize) : ''}</div>
                  </Cell>
                  <Cell>
                    <div className="center-cell">
                      {hasSucceeded && metricsTooltip && (
                        <Tooltip
                          title={<div className="metrics-tooltip-content">{metricsTooltip}</div>}
                          slotProps={{ tooltip: { className: 'metrics-tooltip' } }}
                        >
                          <div>
                            <MetricsIcon />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </Cell>
                  <Cell>
                    <IconButton onClick={(e) => handleClickActionsDetail(e, subSimulation)}>
                      <VerticalDotsIconComponent />
                    </IconButton>
                    <Menu
                      anchorEl={anchorElActionsDetail}
                      open={Boolean(anchorElActionsDetail)}
                      onClose={handleMenuCloseActionsDetail}
                      className="actions-menu"
                    >
                      <MenuItem
                        disabled={isDeleteDisabled}
                        className="actions-menu-item"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (selectedItem) handleDeleteSubSimulation(selectedItem);
                        }}
                      >
                        <Tooltip title={`Delete ${selectedItem?.name}`}>
                          <IconButton aria-label="delete" color="error" disabled={isDeleteDisabled}>
                            <DeleteIconComponent />
                          </IconButton>
                        </Tooltip>
                      </MenuItem>
                      {/* Other menu items */}
                    </Menu>
                  </Cell>

                  <Cell>{''}</Cell>
                </RowComponent>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default SubSimulation;
